import React, { useState, useRef } from 'react';
import { Upload, FileSpreadsheet, X, CheckCircle, AlertCircle, Users, Download } from 'lucide-react';
import { userManagementService, BulkUploadResponse } from '../../services/userManagementService';
import { useNotification } from '../../hooks/useNotification';
import Button from '../UI/Button';
import Modal from '../UI/Modal';

interface BulkUserUploadProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface UploadResult {
  success: boolean;
  data?: BulkUploadResponse['data'];
  error?: string;
}

const BulkUserUpload: React.FC<BulkUserUploadProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const { showNotification } = useNotification();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);
  const [dragActive, setDragActive] = useState(false);

  const handleFileSelect = (file: File) => {
    // Validate file type
    const allowedTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
      'application/vnd.ms-excel' // .xls
    ];
    
    if (!allowedTypes.includes(file.type) && !file.name.match(/\.(xlsx|xls)$/i)) {
      showNotification('Please select a valid Excel file (.xlsx or .xls)', 'error');
      return;
    }
    
    // Validate file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      showNotification('File size must be less than 10MB', 'error');
      return;
    }
    
    setSelectedFile(file);
    setUploadResult(null);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    
    setIsUploading(true);
    try {
      const response = await userManagementService.bulkUploadUsers(selectedFile);
      
      if (response.success) {
        setUploadResult({ success: true, data: response.data });
        showNotification(response.message, 'success');
        onSuccess(); // Refresh the user list
      } else {
        setUploadResult({ 
          success: false, 
          error: response.error?.message || 'Upload failed' 
        });
        showNotification(response.error?.message || 'Upload failed', 'error');
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.error?.message || 'Upload failed';
      setUploadResult({ success: false, error: errorMessage });
      showNotification(errorMessage, 'error');
    } finally {
      setIsUploading(false);
    }
  };

  const handleReset = () => {
    setSelectedFile(null);
    setUploadResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const downloadTemplate = () => {
    // Create a simple CSV template that users can convert to Excel
    const templateData = [
      ['phone', 'email', 'first_name', 'last_name', 'role'],
      ['+1234567890', 'john.doe@example.com', 'John', 'Doe', 'guest'],
      ['+1234567891', 'jane.smith@example.com', 'Jane', 'Smith', 'host'],
      ['+1234567892', 'admin@example.com', 'Admin', 'User', 'admin']
    ];
    
    const csvContent = templateData.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'user_upload_template.csv';
    link.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Bulk Upload Users"
      size="lg"
    >
      <div className="space-y-6">
        {/* Instructions */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="text-sm font-medium text-blue-900 mb-2">Upload Instructions</h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Upload an Excel file (.xlsx or .xls) with user data</li>
            <li>• <strong>Phone number is required</strong> for all users</li>
            <li>• Role will default to "guest" if not specified or invalid</li>
            <li>• Supported column names: phone, email, first_name, last_name, role</li>
            <li>• Maximum file size: 10MB</li>
          </ul>
          <div className="mt-3">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={downloadTemplate}
              className="text-blue-700 border-blue-300 hover:bg-blue-100"
            >
              <Download className="h-4 w-4 mr-2" />
              Download Template
            </Button>
          </div>
        </div>

        {/* File Upload Area */}
        {!uploadResult && (
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              dragActive
                ? 'border-blue-400 bg-blue-50'
                : 'border-gray-300 hover:border-gray-400'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileChange}
              className="hidden"
            />
            
            {selectedFile ? (
              <div className="space-y-4">
                <div className="flex items-center justify-center">
                  <FileSpreadsheet className="h-12 w-12 text-green-500" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">{selectedFile.name}</p>
                  <p className="text-sm text-gray-500">
                    {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
                <div className="flex justify-center space-x-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    Change File
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleReset}
                  >
                    <X className="h-4 w-4 mr-1" />
                    Remove
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-center">
                  <Upload className="h-12 w-12 text-gray-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    Drop your Excel file here, or click to browse
                  </p>
                  <p className="text-sm text-gray-500">
                    Supports .xlsx and .xls files up to 10MB
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                >
                  Select File
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Upload Results */}
        {uploadResult && (
          <div className="space-y-4">
            <div className={`p-4 rounded-lg border ${
              uploadResult.success 
                ? 'bg-green-50 border-green-200' 
                : 'bg-red-50 border-red-200'
            }`}>
              <div className="flex items-center">
                {uploadResult.success ? (
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                ) : (
                  <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
                )}
                <h3 className={`text-sm font-medium ${
                  uploadResult.success ? 'text-green-900' : 'text-red-900'
                }`}>
                  {uploadResult.success ? 'Upload Successful' : 'Upload Failed'}
                </h3>
              </div>
              <p className={`text-sm mt-1 ${
                uploadResult.success ? 'text-green-800' : 'text-red-800'
              }`}>
                {uploadResult.success 
                  ? `Successfully processed ${uploadResult.data?.total_processed} users`
                  : uploadResult.error
                }
              </p>
            </div>

            {uploadResult.success && uploadResult.data && (
              <div className="space-y-4">
                {/* Summary Stats */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <div className="flex items-center">
                      <Users className="h-4 w-4 text-gray-500 mr-2" />
                      <span className="text-sm font-medium text-gray-900">Created</span>
                    </div>
                    <p className="text-2xl font-bold text-gray-900 mt-1">
                      {uploadResult.data.created_count}
                    </p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <div className="flex items-center">
                      <AlertCircle className="h-4 w-4 text-gray-500 mr-2" />
                      <span className="text-sm font-medium text-gray-900">Failed</span>
                    </div>
                    <p className="text-2xl font-bold text-gray-900 mt-1">
                      {uploadResult.data.failed_count}
                    </p>
                  </div>
                </div>

                {/* Role Breakdown */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Role Breakdown</h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-blue-600">
                        {uploadResult.data.role_breakdown.admin}
                      </p>
                      <p className="text-sm text-gray-600">Admins</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-green-600">
                        {uploadResult.data.role_breakdown.host}
                      </p>
                      <p className="text-sm text-gray-600">Hosts</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-purple-600">
                        {uploadResult.data.role_breakdown.guest}
                      </p>
                      <p className="text-sm text-gray-600">Guests</p>
                    </div>
                  </div>
                </div>

                {/* Failed Users Details */}
                {uploadResult.data.failed_users.length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-red-900 mb-3">
                      Failed Users ({uploadResult.data.failed_users.length})
                    </h4>
                    <div className="max-h-40 overflow-y-auto space-y-2">
                      {uploadResult.data.failed_users.map((failed, index) => (
                        <div key={index} className="text-sm">
                          <span className="font-medium text-red-800">
                            Row {failed.row}: {failed.name || 'Unknown'}
                          </span>
                          <p className="text-red-700 text-xs mt-1">{failed.reason}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3">
          {!uploadResult ? (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleUpload}
                disabled={!selectedFile || isUploading}
                loading={isUploading}
              >
                {isUploading ? 'Uploading...' : 'Upload Users'}
              </Button>
            </>
          ) : (
            <Button
              type="button"
              onClick={handleClose}
            >
              Close
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default BulkUserUpload;
